import { useState, useEffect } from "react";
import { debugLog, DEBUG_CATEGORIES } from "@/utils/debug";

export interface DevicePerformanceInfo {
  isLowEnd: boolean;
  prefersReducedMotion: boolean;
  shouldUseOptimizedAnimations: boolean;
  performanceScore: number; // 0-100
  metrics: {
    cores: number;
    memory: number;
    fps: number;
    connectionSpeed: string;
    devicePixelRatio: number;
  };
  detectionComplete: boolean;
}

export function useDevicePerformance() {
  const [performanceInfo, setPerformanceInfo] = useState<DevicePerformanceInfo>({
    isLowEnd: false,
    prefersReducedMotion: false,
    shouldUseOptimizedAnimations: false,
    performanceScore: 100,
    metrics: {
      cores: 4,
      memory: 4,
      fps: 60,
      connectionSpeed: "4g",
      devicePixelRatio: 1,
    },
    detectionComplete: false,
  });

  useEffect(() => {
    debugLog(DEBUG_CATEGORIES.PERF, "Iniciando detecção de performance");

    let frameCount = 0;
    let lastTime = 0;
    let animationFrameId: number;

    // Detectar hardware
    const cores = navigator.hardwareConcurrency || 4;
    const memory = (navigator as any).deviceMemory || 4;
    
    debugLog(DEBUG_CATEGORIES.PERF, `Hardware detectado - Cores: ${cores}, RAM: ${memory}GB`);

    // Detectar velocidade de conexão
    const connection = (navigator as any).connection || (navigator as any).mozConnection || (navigator as any).webkitConnection;
    const connectionSpeed = connection?.effectiveType || "4g";
    const isSlowConnection = connectionSpeed === "slow-2g" || connectionSpeed === "2g";
    
    debugLog(DEBUG_CATEGORIES.PERF, `Conexão detectada: ${connectionSpeed}`);

    // Device pixel ratio
    const devicePixelRatio = window.devicePixelRatio || 1;
    const isHighDPI = devicePixelRatio > 2;
    
    debugLog(DEBUG_CATEGORIES.PERF, `Device Pixel Ratio: ${devicePixelRatio}`);

    // Detectar preferência de movimento reduzido
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let prefersReducedMotion = mediaQuery.matches;
    
    debugLog(DEBUG_CATEGORIES.PERF, `Prefers Reduced Motion: ${prefersReducedMotion}`);

    // Medir FPS durante 1 segundo
    const measureFPS = () => {
      const currentTime = performance.now();
      if (!lastTime) lastTime = currentTime;
      frameCount++;
      const elapsed = currentTime - lastTime;

      if (elapsed >= 1000) {
        const fps = Math.round((frameCount * 1000) / elapsed);
        debugLog(DEBUG_CATEGORIES.PERF, `FPS medido: ${fps}`);
        
        // Calcular score de performance (0-100)
        let score = 100;
        
        // Penalidades por hardware fraco
        if (cores < 4) score -= 20;
        if (cores < 2) score -= 20;
        if (memory < 4) score -= 20;
        if (memory < 2) score -= 20;
        
        // Penalidades por FPS baixo
        if (fps < 60) score -= 10;
        if (fps < 30) score -= 20;
        if (fps < 15) score -= 30;
        
        // Penalidades por conexão lenta
        if (isSlowConnection) score -= 15;
        
        // Penalidades por high DPI (mais pesado de renderizar)
        if (isHighDPI) score -= 5;
        
        // Garantir que score está entre 0 e 100
        score = Math.max(0, Math.min(100, score));
        
        const isLowEndDevice = score < 50;
        
        debugLog(DEBUG_CATEGORIES.PERF, `Score final: ${score}/100 - Low End: ${isLowEndDevice}`);

        setPerformanceInfo({
          isLowEnd: isLowEndDevice,
          prefersReducedMotion,
          shouldUseOptimizedAnimations: isLowEndDevice || prefersReducedMotion,
          performanceScore: score,
          metrics: {
            cores,
            memory,
            fps,
            connectionSpeed,
            devicePixelRatio,
          },
          detectionComplete: true,
        });

        cancelAnimationFrame(animationFrameId);
      } else {
        animationFrameId = requestAnimationFrame(measureFPS);
      }
    };

    // #707: a medição (1 s de rAF + re-render do Provider) ficava no meio do boot e aparecia no TBT.
    // Agora começa só depois do load e de um momento ocioso.
    let idleId: number | undefined;
    let timeoutId: number | undefined;
    let cancelado = false;
    const iniciarMedicao = () => {
      if (cancelado) return;
      animationFrameId = requestAnimationFrame(measureFPS);
    };
    const depoisDoLoad = () => {
      const ric = (window as Window & { requestIdleCallback?: (cb: () => void, o?: { timeout: number }) => number }).requestIdleCallback;
      if (ric) idleId = ric(iniciarMedicao, { timeout: 3000 });
      else timeoutId = window.setTimeout(iniciarMedicao, 1500);
    };
    if (document.readyState === "complete") depoisDoLoad();
    else window.addEventListener("load", depoisDoLoad, { once: true });

    // Iniciar medição de FPS

    // Listener para mudanças em prefers-reduced-motion
    const handleChange = (e: MediaQueryListEvent) => {
      debugLog(DEBUG_CATEGORIES.PERF, `Prefers Reduced Motion mudou para: ${e.matches}`);
      prefersReducedMotion = e.matches;
      setPerformanceInfo(prev => ({
        ...prev,
        prefersReducedMotion: e.matches,
        shouldUseOptimizedAnimations: prev.isLowEnd || e.matches,
      }));
    };
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      cancelado = true;
      window.removeEventListener("load", depoisDoLoad);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
      const cic = (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback;
      if (idleId !== undefined && cic) cic(idleId);
      cancelAnimationFrame(animationFrameId);
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  return performanceInfo;
}
